import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Clock, MessageSquare, Phone, CheckCircle2, ChevronRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { getBlog, BLOGS } from "../data/blogs";
import { SiteHeader, SiteFooter, WA } from "../components/SiteChrome";
import { Seo, faqSchema, breadcrumbSchema, articleSchema } from "../components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const blog = getBlog(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!blog) return <Navigate to="/blog" replace />;

  const related = BLOGS.filter((b) => b.slug !== slug);

  return (
    <div className="min-h-screen flex flex-col font-sans text-[#2D2A26] bg-[#FAF6EF]">
      <Seo
        title={blog.metaTitle}
        description={blog.metaDescription}
        canonical={`/blog/${blog.slug}`}
        jsonLd={[
          articleSchema(blog),
          faqSchema(blog.faqs),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Guides", url: "/blog" },
            { name: blog.h1, url: `/blog/${blog.slug}` },
          ]),
        ]}
      />
      <SiteHeader />

      {/* Hero */}
      <section className="py-14 sm:py-20 bg-[#1A4D45] text-[#FAF6EF]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-5">
          <div className="flex items-center gap-2 text-xs text-[#FAF6EF]/70">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-white">Guides</Link>
          </div>
          <span className="inline-block text-[11px] uppercase tracking-wider font-semibold text-[#C89D3C]">{blog.category}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold leading-tight">{blog.h1}</h1>
          <p className="text-base sm:text-lg text-[#FAF6EF]/85 font-light">{blog.heroSubtitle}</p>
          <div className="flex items-center gap-4 text-xs text-[#FAF6EF]/70 pt-2">
            <span className="inline-flex items-center gap-1"><Clock size={13} /> {blog.readTime}</span>
            <span>Updated {new Date(blog.updated).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-14 sm:py-20 flex-1">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-10">
          <div className="space-y-5 text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
            {blog.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>

          {blog.sections.map((s, i) => (
            <div key={i} className="space-y-4">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A4D45] pt-2">{s.h2}</h2>
              {s.paras.map((p, j) => (
                <p key={j} className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">{p}</p>
              ))}
              {s.list && s.list.length > 0 && (
                <ul className="space-y-2.5 pt-1">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex gap-3 text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                      <CheckCircle2 size={18} className="text-[#C89D3C] shrink-0 mt-0.5" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Inline CTA */}
          <div className="my-8 p-7 rounded-2xl bg-[#FDFBF7] border border-[#C89D3C]/40 text-center space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Book a test with home sample collection</h3>
            <p className="text-sm text-[#4A4743] font-light">We come to your home anywhere in Kolkata and send the report on WhatsApp. Get an honest price before you book.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={WA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C89D3C] text-white hover:bg-[#b08732] font-semibold uppercase tracking-wider text-sm transition">
                <MessageSquare size={16} /> WhatsApp Us
              </a>
              <a href="tel:9007184184" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1A4D45] text-white hover:bg-[#133c32] font-semibold uppercase tracking-wider text-sm transition">
                <Phone size={16} /> Call 7AM–10PM
              </a>
            </div>
          </div>

          {/* FAQ */}
          <div className="space-y-5 pt-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A4D45]">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {blog.faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`f-${idx}`} className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-xl px-5">
                  <AccordionTrigger className="font-serif text-base font-bold text-[#1A4D45] hover:text-[#C89D3C] hover:no-underline text-left py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[#4A4743] font-light leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Related */}
          <div className="space-y-4 pt-6 border-t border-[#E8E2D2]">
            <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Related guides</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((b) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} className="block p-5 rounded-xl bg-[#FDFBF7] border border-[#E8E2D2] hover:border-[#C89D3C]/50 transition">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-[#C89D3C]">{b.category}</span>
                  <p className="font-serif font-bold text-[#1A4D45] mt-1 leading-snug">{b.h1}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
